import { type FC } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import claimedIconUrl from '@wwe-portal/ui/assets/images/roster/claimed-standard.png';

import type { CurrencyType } from '@wwe-portal/entities/currency.type';

import { useMilestone } from './hooks';
import { MilestoneItemIcon } from './milestone-item-icon';
import TooltipWrapper from './tooltip';
import { ProgressBar } from '../../../components/progress';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { Skeleton, TextSkeleton } from '@wwe-portal/ui/components/skeleton';
import { shortenNumber } from '@wwe-portal/utils/number';

export interface MilestonePeriodData {
  id: string;
  periodType: number;
  currentPoint: number;
  endPoint: number;
  title: string;
  imgUrl?: string;
  currencyType?: CurrencyType;
  description?: string;
  isClaimed: boolean;
  endTime: string;
  milestoneId: string;
  hasPin?: boolean;
  updatedAt: number;
  quantity?: number;
}

export interface MilestonePeriodProps {
  data: MilestonePeriodData;
  onClick: () => void;
  onReset: () => void;
}

export interface MilestoneStatusProps {
  count: number;
  milestoneId: string;
}

const MilsetoneSkeleton = () => {
  return (
    <div className="font-poppins text-text300 h-full w-full pr-1 text-xs font-[400] leading-5">
      <div className="flex items-center justify-between">
        <TextSkeleton lineHeight={20} fontSize={12} className="w-20" />

        <div className="relative flex items-center">
          <Skeleton className="h-8 w-9 md:h-9 md:w-10" />
        </div>
      </div>

      <div data-enabled>
        <ProgressBar
          value={0}
          maxValue={100}
          hideValue
          barHeight={10}
          color="linear-gradient(90deg, #FC8154 0%, #FFA800 100.53%)"
          className="my-4"
        />
      </div>

      <div className="flex items-center justify-between">
        <TextSkeleton lineHeight={20} fontSize={12} className="w-10" />
        <TextSkeleton lineHeight={20} fontSize={12} className="w-10" />
      </div>
    </div>
  );
};

const MilestonePeriod: FC<MilestonePeriodProps> = ({
  data: {
    id,
    periodType = 1,
    currentPoint,
    endPoint,
    title,
    imgUrl,
    currencyType,
    isClaimed = false,
    hasPin = false,
    endTime,
    updatedAt = Date.now(),
  },
  onClick,
  onReset,
}) => {
  const { isHydrated } = useResponsive();
  const { timeLeft, displayPoint } = useMilestone({
    data: { periodType, currentPoint, endTime, updatedAt },
    onReset,
  });

  if (!isHydrated) return <MilsetoneSkeleton />;
  if (new Date(endTime).getTime() < new Date().getTime()) return null;

  return (
    <div
      className="font-poppins text-text300 h-full w-full pr-1 text-xs font-[400] leading-5"
      id={id}
    >
      <div className="flex items-center justify-between">
        <span>{Boolean(timeLeft) && <span>{timeLeft} Left</span>}</span>

        <ClickableDiv className="relative flex items-center" onClick={onClick}>
          <MilestoneItemIcon
            className="h-8 w-9 md:h-9 md:w-10"
            currencyType={currencyType}
            displayPoint={displayPoint}
            imgUrl={imgUrl}
            isClaimed={isClaimed}
          />
          {Boolean(displayPoint) && hasPin && !isClaimed && (
            <div className="absolute left-[-12px] h-[6px] w-[6px] rounded-[3px] bg-[#CE5C5C]" />
          )}
          {Boolean(displayPoint) && isClaimed && (
            <img
              alt="logo"
              className="md:w-13 absolute right-[-4px] top-0 h-8 w-12 cursor-pointer object-contain md:h-9"
              src={claimedIconUrl}
            />
          )}
        </ClickableDiv>
      </div>

      <div data-enabled>
        <TooltipWrapper
          content={
            <span>
              {displayPoint}/{endPoint} XP
            </span>
          }
        >
          <ProgressBar
            value={Math.round((displayPoint / endPoint) * 100)}
            maxValue={100}
            hideValue
            barHeight={10}
            color="linear-gradient(90deg, #FC8154 0%, #FFA800 100.53%)"
            className="my-4"
          />
        </TooltipWrapper>
      </div>

      <div className="flex items-center justify-between">
        <span>{title}</span>
        <span>
          {shortenNumber(displayPoint)}/{shortenNumber(endPoint)} XP
        </span>
      </div>
    </div>
  );
};

export default MilestonePeriod;
